<script setup lang="ts">
import { BaseButton, Modal, openConfirm, Typography } from 'src/shared/ui';
import {
  clientTransactionTypeTranslations,
  translations,
} from 'src/logic/constants/translations.constants';
import ClientInfoBlock from 'src/shared/ui/client-info-block/client-info-block.vue';
import { storeToRefs } from 'pinia';
import { useClientStore } from 'src/logic/stores/clients.store';
import { useRoute } from 'vue-router';
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import router from 'src/router';
import { useAuthStore } from 'src/logic/stores/auth.store';
import {
  openClientDetails,
  openClientDetailsInNewTab,
} from 'src/logic/functions/route-to-client.function';

const clientStore = useClientStore();
const { user } = storeToRefs(useAuthStore());
const { clientMiniaturePopupIsOpened, clientByIdForMiniature, clientIdForMiniaturePopup } =
  storeToRefs(clientStore);
const { fnChangeClientMiniaturePopupState, fnGetClientById } = clientStore;
const route = useRoute();
const clientId = route.params.miniatureClientId ?? clientIdForMiniaturePopup.value;
const remarksRef = ref<HTMLElement | null>(null);
const remarksHeightState = ref(true);
const showRemarksMore = ref(false);
const remarksHeight = ref(0);
let observer: any = null;

const accessToDetails = computed(() => {
  if (clientByIdForMiniature.value) {
    return (
      user.value?.id === clientByIdForMiniature.value.creator?.id ||
      user.value?.id === clientByIdForMiniature.value.receiver?.id
    );
  }
  return false;
});

function setShowRemarksMore(): void {
  if (!remarksRef.value) {
    showRemarksMore.value = false;
  } else {
    showRemarksMore.value = remarksRef.value.offsetHeight > 107;
  }
}

const toggleRemarksMaxHeight = (): void => {
  const height = remarksHeightState.value ? 'max-content' : '108px';
  if (remarksRef.value) {
    remarksRef.value.style.setProperty('--remarks-max-height', `${height}`);
    remarksHeightState.value = !remarksHeightState.value;
  }
};

function formatPrice(price: number | null | undefined): string {
  if (typeof price !== 'number') return '_';

  const str = price!.toString();
  if (str.length < 3) return str;

  const result = [];
  let count = 0;

  for (let i = str.length - 1; i >= 0; i--) {
    result.unshift(str[i]);
    count++;
    if (count === 3 && i !== 0) {
      result.unshift(' ');
      count = 0;
    }
  }

  return result.join('');
}

async function openDetails(): Promise<void> {
  await openClientDetails(clientId.toString(), false);
  onCloseModal();
}

function remarksSizeHandling(): void {
  if (remarksRef.value) {
    observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newHeight = entry.contentRect.height;
        if (newHeight !== remarksHeight.value) {
          remarksHeight.value = newHeight;
          setShowRemarksMore();
        }
      }
    });
    observer.observe(remarksRef.value);
  }
}

function onCloseModal(): void {
  if (route.params.miniatureClientId !== undefined) router.push('/clients');
  fnChangeClientMiniaturePopupState(false);
}

onMounted(async () => {
  const { error } = await fnGetClientById(clientId as string, true);
  if (error) {
    await openConfirm({
      title: error.detail,
      confirmButtonText: 'Закрыть',
      cancelButtonText: null,
      hideCloseIcon: true,
      maxWidth: '646px',
      zIndex: 6,
    });
    onCloseModal();
  }
  fnChangeClientMiniaturePopupState(true);
  await nextTick();
  if (remarksRef.value) {
    remarksSizeHandling();
  }
});

onUnmounted(() => {
  if (observer) observer.disconnect();
  clientByIdForMiniature.value = undefined;
  clientIdForMiniaturePopup.value = null;
  clientMiniaturePopupIsOpened.value = false;
  clientStore.fnChangeClientMiniaturePopupState(false);
});
</script>

<template>
  <Modal
    :open="clientMiniaturePopupIsOpened && !!clientByIdForMiniature"
    disable-portal
    @update:open="() => onCloseModal()"
    :hide-close-icon="false"
    size="m"
    :close-on-click-outside="true"
  >
    <template #icon>
      <div class="title-container">
        <typography :class="'client-name'" as="div" variant="h6">
          {{ `  ${clientByIdForMiniature!.first_name ?? ''}  `.trim() }}
        </typography>
        <BaseButton
          type="primary"
          size="subm"
          :class="[
            'status-btn ',
            { 'blue-btn': clientByIdForMiniature!.status === 0 },
            { 'green-btn': clientByIdForMiniature!.status === 10 },
            { 'orange-btn': clientByIdForMiniature!.status === 20 },
            { 'grey-btn': clientByIdForMiniature!.status === 30 },
            { 'red-btn': clientByIdForMiniature!.status === 40 },
          ]"
        >
          {{ translations[`clientStatus${clientByIdForMiniature!.status}`] }}
        </BaseButton>
      </div>
    </template>
    <div class="client-miniature-container">
      <div class="info-grid">
        <ClientInfoBlock
          :title="'Тип сделки'"
          :subtitle="clientTransactionTypeTranslations[clientByIdForMiniature!.transaction_type]"
        />
        <ClientInfoBlock
          v-if="clientByIdForMiniature!.country"
          :title="'Страна'"
          :subtitle="clientByIdForMiniature!.country.name"
        />
        <ClientInfoBlock
          v-if="clientByIdForMiniature!.region"
          :title="'Регион'"
          :subtitle="clientByIdForMiniature!.region.name"
        />
        <ClientInfoBlock
          v-if="clientByIdForMiniature!.city"
          :title="'Город'"
          :subtitle="clientByIdForMiniature!.city.name"
        />
        <ClientInfoBlock
          :title="'Тип недвижимости'"
          :subtitle="translations[clientByIdForMiniature!.property_type]"
        />
        <ClientInfoBlock
          :title="'Подтип недвижимости'"
          :subtitle="translations[clientByIdForMiniature!.property_subtype]"
        />
        <ClientInfoBlock
          :title="
            clientByIdForMiniature!.property_subtype === 'land_plot' ||
            clientByIdForMiniature!.property_subtype === 'commercial_land_plot'
              ? 'Площадь земельного участка'
              : 'Общая площадь'
          "
          :subtitle="
            clientByIdForMiniature!.property_subtype === 'land_plot' ||
            clientByIdForMiniature!.property_subtype === 'commercial_land_plot'
              ? formatPrice(clientByIdForMiniature!.total_area) + ' сот'
              : formatPrice(clientByIdForMiniature!.total_area) + ' м²'
          "
        />
        <ClientInfoBlock
          :title="
            clientByIdForMiniature!.transaction_type === 'purchase'
              ? 'Цена, руб.'
              : 'Месячный арендный платёж, руб.'
          "
          :subtitle="formatPrice(clientByIdForMiniature!.price) + ' ₽'"
        />
        <ClientInfoBlock
          v-if="clientByIdForMiniature!.mortgage_approved !== null"
          :title="'Ипотека одобрена'"
          :subtitle="clientByIdForMiniature!.mortgage_approved ? 'Да' : 'Нет'"
        />
        <ClientInfoBlock
          v-if="clientByIdForMiniature!.cash_amount || clientByIdForMiniature!.cash_amount === 0"
          :title="'Сумма наличными, руб.'"
          :subtitle="formatPrice(clientByIdForMiniature!.cash_amount) + ' ₽'"
        />
      </div>

      <div
        :class="[
          { 'info-grid full-size-block remarks-container': clientByIdForMiniature!.remarks },
        ]"
        ref="remarksRef"
        style="--remarks-max-height: 108px"
      >
        <ClientInfoBlock
          v-if="clientByIdForMiniature!.remarks"
          :title="'Примечание'"
          :subtitle="clientByIdForMiniature!.remarks"
        />
      </div>
      <div class="info-block-actions">
        <typography
          v-if="showRemarksMore"
          @click="toggleRemarksMaxHeight"
          as="p"
          :variant="'body-1'"
          class="show-more"
        >
          {{ remarksHeightState ? 'Показать полностью' : 'Свернуть' }}
        </typography>
      </div>

      <div class="modal-buttons" v-if="accessToDetails">
        <BaseButton
          type="primary"
          size="xl"
          class="modal-button"
          @click="openDetails()"
          @click.middle="openClientDetailsInNewTab(clientId.toString(), false)"
        >
          Перейти в полный режим
        </BaseButton>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
.client-miniature-container {
  width: 755px;

  .remarks-container {
    max-height: var(--remarks-max-height);
  }

  .info-block-actions {
    display: flex;
    justify-content: flex-end;
    padding-top: var(--spacing-l);

    .show-more {
      color: var(--palette-blue-light);
      cursor: pointer;
    }
  }

  .info-grid {
    width: 100%;
    display: grid;
    margin-top: 12px;
    grid-template-columns: repeat(3, 1fr);
    grid-column-gap: 0;
    grid-row-gap: var(--spacing-xl);
  }

  .full-size-block {
    display: flex;
    flex-direction: column;
  }

  .comment-container {
    margin-top: 24px;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 100%);
      width: calc(100% - 6px);
      height: 24px;
    }

    &::after {
      content: '';
      position: absolute;
      top: 16px;
      background: linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, #ffffff 100%);
      width: calc(100% - 6px);
      height: 14px;
    }

    .comment-title {
      color: var(--palette-graphite);
      position: absolute;
      z-index: 1;
      background-color: white;
      width: 100%;
    }

    .comment-field {
      max-height: 224px;
      min-height: 75px;
      overflow: auto;
      padding: 20px 0 16px;

      &::-webkit-scrollbar-thumb {
        background-color: #d8d8d8;
        border-radius: 2px;
      }

      &::-webkit-scrollbar-track {
        background-color: #fff0;
      }

      &::-webkit-scrollbar {
        width: 2px;
      }
    }
  }

  .modal-buttons {
    margin-top: var(--spacing-7xl);
  }
}

.client-miniature-container-nd {
  width: 570px;

  .modal-title {
    text-align: center;
  }

  .modal-buttons {
    display: flex;
    justify-content: center;
    margin-top: var(--spacing-7xl);

    .modal-button {
      width: 230px;
      height: 44px;
    }
  }
}

.status-btn {
  cursor: inherit;
}

.title-container {
  display: flex;
  gap: var(--spacing-xl);

  .client-name {
    max-width: 500px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.blue-btn {
  background-color: var(--palette-blue-light);
  color: var(--palette-white);
}

.green-btn {
  background-color: var(--palette-green-sky);
  color: var(--palette-green-text);
}

.orange-btn {
  background-color: var(--palette-orange-sky);
  color: var(--palette-orange-text);
}

.grey-btn {
  background-color: var(--palette-gray);
  color: var(--palette-black);
}

.red-btn {
  background-color: var(--palette-red);
  color: var(--palette-white);
}
</style>
